import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import useSettings from '../hooks/useSettings';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Tag, Package, Truck, ClipboardList, ShoppingCart, ArrowRight } from 'lucide-react';

// ── Components ────────────────────────────────────────────────────────────────

function FlowRow({ steps, showNote = false }) {
  return (
    <div className="flex flex-wrap items-start gap-2">
      {steps.map((step, i) => (
        <div key={i} className="flex items-start gap-2">
          <div className="flex flex-col items-center">
            <span className={`text-xs font-medium px-3 py-1.5 rounded-lg whitespace-nowrap ${step.style}`}>
              {step.label}
            </span>
            {showNote && step.note && (
              <span className="text-xs text-gray-400 mt-1 text-center">{step.note}</span>
            )}
          </div>
          {i < steps.length - 1 && <ArrowRight size={14} className="text-gray-300 mt-1.5 shrink-0" />}
        </div>
      ))}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
      <p className="text-sm font-semibold text-gray-800">{title}</p>
      {children}
    </div>
  );
}

function Rule({ emoji, text }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="text-base leading-tight">{emoji}</span>
      <p className="text-sm text-gray-600 leading-snug">{text}</p>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Guide() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  usePageTitle('Guide');
  const { simpleView } = useSettings();

  const SETUP_STEPS = [
    { icon: Tag,           color: 'bg-blue-50 text-blue-600 border-blue-200',       label: t('nav.categories'),     note: t('guide.optional') },
    { icon: Package,       color: 'bg-indigo-50 text-indigo-600 border-indigo-200', label: t('nav.products'),       note: t('guide.addSellingPrice') },
    { icon: Truck,         color: 'bg-orange-50 text-orange-600 border-orange-200', label: t('nav.sourcers'),       note: t('guide.yourSuppliers'),     simpleHidden: true },
    { icon: ClipboardList, color: 'bg-amber-50 text-amber-700 border-amber-200',    label: t('nav.sourcingOrders'), note: t('guide.receivedAddsStock'), simpleHidden: true },
    { icon: ShoppingCart,  color: 'bg-green-50 text-green-700 border-green-200',    label: t('nav.orders'),         note: t('guide.readyToSell') },
  ].filter(step => !(simpleView && step.simpleHidden));

  const ORDER_FLOW = [
    { label: t('status.PENDING'),    style: 'bg-gray-100 text-gray-700',    note: null },
    { label: t('status.CONFIRMED'),  style: 'bg-blue-50 text-blue-700',     note: null },
    { label: t('status.ON_THE_WAY'), style: 'bg-purple-50 text-purple-700', note: t('guide.stockDeducted') },
    { label: t('status.DELIVERED'),  style: 'bg-green-50 text-green-700',   note: null },
  ];

  const ORDER_EXITS = [
    { label: t('status.CANCELLED'), style: 'bg-red-50 text-red-600',       note: t('guide.cancelledNote') },
    { label: t('status.RETURNED'),  style: 'bg-orange-50 text-orange-600', note: t('guide.returnedNote') },
  ];

  const SOURCING_FLOW = [
    { label: t('status.PENDING'),  style: 'bg-gray-100 text-gray-700',  note: t('guide.editableHereOnly') },
    { label: t('status.ORDERED'),  style: 'bg-blue-50 text-blue-700',   note: null },
    { label: t('status.RECEIVED'), style: 'bg-green-50 text-green-700', note: t('guide.sourcingAddStock') },
  ];

  return (
    <div className="p-6 max-w-4xl space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/')} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{t('guide.title')}</h1>
          <p className="text-sm text-gray-400">{t('guide.subtitle')}</p>
        </div>
      </div>

      {/* Setup order */}
      <Card title={t('guide.setupOrder')}>
        <div className="flex items-start gap-2">
          {SETUP_STEPS.map((step, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="flex flex-col items-center gap-1.5">
                <div className={`flex items-center gap-1.5 border px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${step.color}`}>
                  <step.icon size={13} />
                  {step.label}
                </div>
                <p className="text-xs text-gray-400 text-center leading-snug max-w-[90px]">{step.note}</p>
              </div>
              {i < SETUP_STEPS.length - 1 && <ArrowRight size={13} className="text-gray-300 shrink-0 mt-2" />}
            </div>
          ))}
        </div>
      </Card>

      {/* Customer order flow */}
      <Card title={t('guide.customerOrderFlow')}>
        <FlowRow steps={ORDER_FLOW} showNote />
        <div className="border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-400 mb-2">{t('guide.canAlsoExitTo')}</p>
          <div className="flex flex-wrap gap-3">
            {ORDER_EXITS.map((s, i) => (
              <div key={i} className="flex flex-col items-start gap-1">
                <span className={`text-xs font-medium px-3 py-1.5 rounded-lg ${s.style}`}>{s.label}</span>
                <span className="text-xs text-gray-400">{s.note}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
          {t('guide.orderEditNote')}
        </p>
      </Card>

      {/* Sourcing order flow */}
      {!simpleView && (
        <Card title={t('guide.sourcingOrderFlow')}>
          <FlowRow steps={SOURCING_FLOW} showNote />
          <p className="text-xs text-gray-400 bg-gray-50 rounded-lg px-3 py-2">
            {t('guide.sourcingEditNote')}
          </p>
        </Card>
      )}

      {/* Key rules */}
      <Card title={t('guide.keyRules')}>
        <div className="space-y-3">
          <Rule emoji="👤" text={t('guide.rule1')} />
          <Rule emoji="🏷️" text={t('guide.rule2')} />
          <Rule emoji="📦" text={t('guide.rule3')} />
          <Rule emoji="💰" text={t('guide.rule4')} />
        </div>
      </Card>
    </div>
  );
}
